import { createStore, applyMiddleware, combineReducers } from 'redux';
import { thunk } from 'redux-thunk'; // Note: update to use named import
import { persistStore, persistReducer, createTransform } from 'redux-persist';
import { tabWorkspaceStorage } from '../utils/tabWorkspace';

import imageReducer from './slices/imageSlice';
import projectsReducer from './slices/projectsSlice';
import userReducer from './slices/userSlice';
import jobsReducer from './slices/jobsSlice';

// Don't persist a COMPLETED tech pack jobResult. It is frozen at generation
// time, so after a refresh it re-hydrates stale data that predates any edits
// the user saved to Mongo since — the root cause of the "Product Info / Size
// Table edits gone after refresh" bug class (TechPackUI's liveGenerationRef
// guard remains as belt-and-suspenders). Mongo is the authority once the job
// has succeeded; the restore-on-mount flow loads it from there.
//
// Deliberately scoped: only the techpack job, and only status 'succeeded'.
//   - pending/polling keep their (empty) state so polling can resume.
//   - Other features' jobs are untouched; some of their UIs still read
//     persisted results on purpose.
const stripCompletedTechpackResult = createTransform(
  // transform state on its way INTO storage
  (inboundState) => {
    const techpack = inboundState?.techpack;
    if (techpack && techpack.status === 'succeeded' && techpack.result) {
      return {
        ...inboundState,
        techpack: { ...techpack, result: null },
      };
    }
    return inboundState;
  },
  // no transform on rehydration
  null,
  { whitelist: ['jobs'] }
);

// The images slice accumulates EVERY upload across sessions — including
// base64Data payloads — with no cap. Left unchecked, persist:root eventually
// blows the storage quota, after which redux-persist's writes
// fail silently and every persisted behavior (tech pack job resume, stale-job
// guards) stops working "out of nowhere". Strip heavy payloads and cap the
// history depth before anything is written.
const slimImagesForPersist = createTransform(
  (inboundState) => {
    try {
      const strip = (item) => {
        if (!item || typeof item !== 'object') return item;
        const { base64Data, ...rest } = item;
        for (const k of Object.keys(rest)) {
          if (typeof rest[k] === 'string' && rest[k].startsWith('data:')) rest[k] = null;
        }
        return rest;
      };
      return {
        ...inboundState,
        uploadedImages: (inboundState.uploadedImages || []).slice(-8).map(strip),
        editHistory: (inboundState.editHistory || []).slice(-6).map(strip),
        variations: (inboundState.variations || []).slice(-8).map(strip),
        currentImage: strip(inboundState.currentImage),
      };
    } catch (e) {
      return inboundState;
    }
  },
  null,
  { whitelist: ['images'] }
);

// Jobs and their input images belong to one TAB, but must outlive it: polling
// drives generation, so work in flight when a tab closes is adopted by the
// next new tab (utils/tabWorkspace.js). The key stays 'root' — storage maps it
// to one localStorage entry per tab — so older builds' saved work is adopted
// too instead of being stranded.
export const workspacePersistConfig = {
  key: 'root',
  storage: tabWorkspaceStorage,
  whitelist: ['images', 'jobs'], // Persist images and jobs slices
  transforms: [stripCompletedTechpackResult, slimImagesForPersist],
  // A failed persist write means job resume and every other persisted
  // behavior silently dies — make it LOUD so it's diagnosable from a user's
  // console instead of presenting as "refresh randomly loses my generation".
  writeFailHandler: (err) => {
    console.error('[REDUX-PERSIST] WRITE FAILED — persisted state (incl. tech pack job resume) is NOT being saved:', err);
  },
};

const rootReducer = combineReducers({
  images: imageReducer,
  projects: projectsReducer,
  user: userReducer,
  jobs: jobsReducer,
});

const persistedReducer = persistReducer(workspacePersistConfig, rootReducer);

export const store = createStore(
  persistedReducer,
  applyMiddleware(thunk)
);

export const persistor = persistStore(store);
